import React from 'react';
import { GripVertical, ArrowUp, ArrowDown, Copy, Trash2 } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * The selectable/movable frame around every block on the canvas. Chrome only
 * — the block's rendering comes from the shared BlockView / type editors, so
 * the content inside the frame is exactly what the public site renders.
 *
 * The toolbar floats at the frame's top-right on hover/selection: drag
 * handle (dnd-kit), move up/down (keyboard-friendly duplicates of drag),
 * duplicate, delete.
 */

interface Props {
  blockId: string;
  selected: boolean;
  onSelect: () => void;
  onMoveUp: (() => void) | null;
  onMoveDown: (() => void) | null;
  onDuplicate: () => void;
  onDelete: () => void;
  children: React.ReactNode;
}

export default function BlockFrame({
  blockId,
  selected,
  onSelect,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDelete,
  children,
}: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: blockId });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.55 : undefined,
      }}
      className={`canvas-block ${selected ? 'canvas-block--selected' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      data-block-id={blockId}
    >
      <div className="canvas-block__toolbar" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="canvas-block__tool canvas-block__tool--drag"
          aria-label="Drag to reorder"
          title="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className="canvas-block__tool"
          aria-label="Move up"
          title="Move up"
          disabled={!onMoveUp}
          onClick={() => onMoveUp && onMoveUp()}
        >
          <ArrowUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className="canvas-block__tool"
          aria-label="Move down"
          title="Move down"
          disabled={!onMoveDown}
          onClick={() => onMoveDown && onMoveDown()}
        >
          <ArrowDown className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className="canvas-block__tool"
          aria-label="Duplicate block"
          title="Duplicate"
          onClick={onDuplicate}
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className="canvas-block__tool canvas-block__tool--danger"
          aria-label="Delete block"
          title="Delete"
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      {children}
    </div>
  );
}
